import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import GenericPromises from "../../../../api/GenericPromises";
import useSnackBar from "../../../../components/Commons/SnackBar/useSnackBar";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Section } from "../../../../interfaces/Stocks/Warehouses/sections";
import { useEffect, useState } from "react";
import { useCompanyStore } from "../../../../store/AuthStore";
import { Header } from "../../../../components/Header";
import { TextField } from "@mui/material";
import { FormsErrors } from "../../../../hooks/Forms/FormsErrors";
import { ButtonStyled, PrimaryButton } from "../../../../theme/buttons";

export const AddSections = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const { GenericPostResource } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { companyState } = useCompanyStore();
  const { GetError } = FormsErrors();
  const [loadingPost, setLoadingPost] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Section>({
    defaultValues: {
      section_name: "",
      section_description: "",
      upper_limit_of_items: 0,
      lower_limit_of_items: 0,
    }
  });
  const onSubmit: SubmitHandler<Section> = (data) => onPost(data);

  const onPost = (data: Section) => {
    let myData = {
      section_name: data.section_name,
      section_description: data.section_description,
      upper_limit_of_items: data.upper_limit_of_items,
      lower_limit_of_items: data.lower_limit_of_items,
      section_location_id: data.section_location_id ?? null,
    }
    setLoadingPost(true);
    GenericPostResource("/sections", myData)
      .then((response) => {
        navigate("/sections", { state: { method: "add" } });
      })
      .catch((error) => {
        setLoadingPost(false);
        showSnackBar(error.message, 'error');
      });
  }

  const onCancel = () => {
    navigate("/sections", { state: {} });
  }

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("sections.title")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header title={t("sections.title-view")} />
      <div className="d-flex flex-column w-100 align-items-center">
        <div className='d-flex justify-content-center custom-card-view w-75'>
          <form onSubmit={handleSubmit(onSubmit)} className='mt-4 w-100'>
            <div className='d-flex'>

              <div className='w-50'>
                <Controller
                  name="section_name"
                  control={control}
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      label={`${t("sections.fields.section_name")}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.section_name}
                      helperText={GetError(errors.section_name?.type)}
                      size="small"
                      style={{ width: "100%" }}
                      sx={{ paddingRight: 2 }}
                    />
                  }
                />
              </div>

              <div className='w-50'>
                <Controller
                  name="section_description"
                  control={control}
                  rules={{ required: true, maxLength: 200 }}
                  render={({ field }) =>
                    <TextField
                      variant="filled"
                      label={`${t("sections.fields.section_description")}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={(event) => { field.onChange(event.target.value) }}
                      error={!!errors.section_description}
                      helperText={GetError(errors.section_description?.type)}
                      size="small"
                      style={{ width: "100%" }}
                    />
                  }
                />
              </div>

            </div>

            <div className='d-flex mt-2'>
              <Controller
                name="upper_limit_of_items"
                control={control}
                rules={{}}
                render={({ field }) =>
                  <TextField
                    variant="filled"
                    label={`${t("sections.fields.upper_limit_of_items")}`}
                    type='number'
                    ref={field.ref}
                    sx={{
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                      "& input[type=number]": { MozAppearance: "textfield", }, paddingRight: 2,
                    }}
                    value={field.value}
                    onChange={(event) => { field.onChange(event.target.value) }}
                    error={!!errors.upper_limit_of_items}
                    helperText={GetError(errors.upper_limit_of_items?.type)}
                    size="small"
                    style={{ width: "50%" }}
                  />
                }
              />
              <Controller
                name="lower_limit_of_items"
                control={control}
                rules={{}}
                render={({ field }) =>
                  <TextField
                    variant="filled"
                    label={`${t("sections.fields.lower_limit_of_items")}`}
                    type='number'
                    ref={field.ref}
                    sx={{
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                      "& input[type=number]": { MozAppearance: "textfield", },
                    }}
                    value={field.value}
                    onChange={(event) => { field.onChange(event.target.value) }}
                    error={!!errors.lower_limit_of_items}
                    helperText={GetError(errors.lower_limit_of_items?.type)}
                    size="small"
                    style={{ width: "50%" }}
                  />
                }
              />
            </div>
            <div className='d-flex flex-row-reverse mt-1'>
              <PrimaryButton type='submit' variant='outlined' className='m-1' disabled={loadingPost}>{t("generic.buttons.accept")}</PrimaryButton>
              <ButtonStyled variant='contained' className='m-1' onClick={onCancel}>{t("generic.buttons.cancel")}</ButtonStyled>
            </div>
          </form>
        </div>

        <SnackbarComponent />
      </div>
    </>
  )
}
